import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
    TextField,
    Typography,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    MenuItem,
    Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

const buildPassengerSchema = (t) => Yup.object().shape({
    ticketType: Yup.number().required(t("passenger.ticketTypeRequired")),
});

export default function PassengerForm({
    passengerIndex,
    totalPassengers,
    onChange,
    defaultValues,
    ticketTypes = [],
    isRoundTrip = false,
}) {
    const { t } = useTranslation();
    const {
        control,
        setValue,
        formState: { errors },
        watch,
    } = useForm({
        resolver: yupResolver(buildPassengerSchema(t)),
        defaultValues: {
            ticketType:
                ticketTypes.find((tt) => tt.ticketTypeId === 1)?.ticketTypeId ??
                ticketTypes[0]?.ticketTypeId ??
                "",
            ...defaultValues,
        },
        mode: "onBlur",
    });

    const ticketType = watch("ticketType");

    useEffect(() => {
        if (ticketTypes.length === 0) return;
        const isValid = ticketTypes.some((tt) => tt.ticketTypeId === ticketType);
        if (!isValid) {
            const base = ticketTypes.find((tt) => tt.ticketTypeId === 1) ?? ticketTypes[0];
            if (base) setValue("ticketType", base.ticketTypeId);
        }
    }, [ticketTypes, ticketType, setValue]);

    useEffect(() => {
        onChange(passengerIndex, { ticketType });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketType, passengerIndex]);

    const isFirst = passengerIndex === 0;

    const selectedType = ticketTypes.find((tt) => tt.ticketTypeId === ticketType);
    const isBasicType = !selectedType || selectedType.ticketTypeId === 1;
    const discountKey = selectedType?.name ? `discounts.${selectedType.name}` : null;
    const discountRemark = (!isBasicType && discountKey) ? t(discountKey, { defaultValue: "" }) : "";

    return (
        <Accordion defaultExpanded={isFirst} sx={{ borderRadius: 2, mb: 1 }}>
            <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Icon icon="mdi:account" width={24} />
                    <Typography sx={{ fontWeight: 600 }}>
                        {t("passenger.passenger")} {passengerIndex + 1}
                        {totalPassengers > 1 ? ` ${t("passenger.of")} ${totalPassengers}` : ""}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    <Controller
                        name="ticketType"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                select
                                fullWidth
                                label={`${t("passenger.ticketType")} *`}
                                error={!!errors.ticketType}
                                helperText={errors.ticketType?.message}
                                onChange={(e) => field.onChange(e)}
                            >
                                {ticketTypes.length === 0 && (
                                    <MenuItem disabled value="">
                                        {t("booking.noTicketTypesAvailable")}
                                    </MenuItem>
                                )}
                                {ticketTypes.map((type) => {
                                    // Za povratnu kartu cena je priceReturnTicket, inače priceOneWay.
                                    const displayPrice = isRoundTrip ? type.priceReturnTicket : type.priceOneWay;
                                    return (
                                        <MenuItem key={type.ticketTypeId} value={type.ticketTypeId}>
                                            {type.name}
                                            {typeof displayPrice === "number"
                                                ? ` — ${displayPrice} RSD`
                                                : ""}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        )}
                    />
                    {discountRemark && (
                        <Alert
                            severity="info"
                            icon={<Icon icon="mdi:information-outline" width={20} />}
                            sx={{ borderRadius: 2, py: 0.5 }}
                        >
                            <Typography variant="body2">{discountRemark}</Typography>
                        </Alert>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}
